import React, { useState, useContext } from 'react';
import { useQuery } from '@apollo/react-hooks';
import { RewardsContext } from '../../context/RewardsContext/RewardsContext';
import RDFFragment from '../../components/RDFFragment/RDFFragment';
import RDFSelectionLegal from '../../components/RDFSelectionLegal/RDFSelectionLegal';
import {
  RECOMMENDED_DATE_FUND,
  RECOMMENDED_DATE_FUND_NO_SUMMIT
} from './queries';
import FundNameSection from '../../components/FundNameSection/FundNameSection';
import FundCompositionSection from '../../components/FundCompositionSection/FundCompositionSection';
import QuickView from '../../components/QuickView/QuickView';
import { RETIREMENT_AGE } from '../../flows/shared/utils';
import { meetsSummitIClassMin } from '../../flows/shared/summit.testers';
import PhoneNumber from '../../components/PhoneNumber/PhoneNumber';
import Heading from '../../components/Typography/Heading/Heading';
import SubHeading from '../../components/Typography/SubHeading/SubHeading';
import { MFBeaconButton } from '../../components/Beacon/BeaconComponents';
import styles from './style.css';
import { DEFAULT_LOADING_MESSAGE } from '../../core/flow-engine';
import Loader from '../../components/Loader/Loader';
import { generateGeneralTestID } from '../../generateAutomationTestIDs';

/* eslint-disable complexity */
// TODO - @Heather: Fix complexity of screen in terms of utilizing summit rewards
const RDFScreen = ({
  back,
  next,
  appState: {
    store: { amount, app, loadedData: { iClassFundDetails = {} } = {} }
  },
  flowProps: { isReadyChoice = false }
}) => {
  const [fundListDisplay, toggleFundListDisplay] = useState(false);
  const [openQuickViewFund, setOpenQuickViewFund] = useState(false);

  const { rewardsAvailable = {} } = useContext(RewardsContext) || {};

  const dob = app?.customer?.dob;

  const { iClassFunds: iClassFundReward } = rewardsAvailable;

  const { loading, error, data } = useQuery(
    isReadyChoice ? RECOMMENDED_DATE_FUND_NO_SUMMIT : RECOMMENDED_DATE_FUND,
    {
      variables: {
        birthDate: dob,
        retirementAge: RETIREMENT_AGE,
        amountToInvest: amount,
        ...(!isReadyChoice && {
          isSummitMember: meetsSummitIClassMin(iClassFundReward, amount)
        })
      }
    }
  );

  if (loading) return <Loader message={DEFAULT_LOADING_MESSAGE} />;
  if (error) return `Error! ${error}`;

  const quickViewFund = {
    ...data.recommendedDateFund,
    correspondingFund: iClassFundDetails,
    amount
  };

  const title = <>We recommend the {quickViewFund.name}</>;

  const subTitle = (
    <>
      This recommendation is based on your date of birth of{' '}
      <span className="blocked_data">{dob}</span>
      , investment amount, <br />
      and an assumed goal of retirement at or near age 65.
    </>
  );

  const handleButtonClick = () => {
    const rdfMethodPDFUrl =
      'https://www.troweprice.com/content/dam/iinvestor/products-and-services/methodology/rfrs-methodology.pdf';
    window.open(rdfMethodPDFUrl, '_blank');
  };

  return (
    <div className="rdf-selection-container">
      <Heading text={title} />

      <SubHeading text={subTitle} />

      <div className="contents">
        <FundNameSection {...quickViewFund} />

        <FundCompositionSection
          fund={quickViewFund}
          openQuickViewFund={openQuickViewFund}
          setOpenQuickViewFund={setOpenQuickViewFund}
          RDFData
        />
      </div>

      {openQuickViewFund && (
        <div>
          <QuickView
            fund={quickViewFund}
            onClose={() => setOpenQuickViewFund(!openQuickViewFund)}
          />
        </div>
      )}
      <div className="bottom">
        <div className="note">
          We recommend only the Retirement Fund shown above for you. But you may
          select another fund.
        </div>
        <div className="fund-links">
          <span className="rdf-fund-links">
            <MFBeaconButton
              type="button"
              id="viewAllRetirementFundsBtn"
              data-name={generateGeneralTestID(
                'RDF',
                'ViewAllRetirementFundsButton'
              )}
              className="override-beacon-button-padding"
              onClick={() => toggleFundListDisplay(!fundListDisplay)}
              variant="tertiary"
              label="View All Retirement Funds"
            />
          </span>
          <span className="rdf-fund-links">
            <MFBeaconButton
              type="button"
              id="viewAllFundsBtn"
              data-name={generateGeneralTestID('RDF', 'ViewAllFundsButton')}
              className="override-beacon-button-padding"
              onClick={next('byop')}
              variant="tertiary"
              label="View All Funds"
            />
          </span>
          <span className="rdf-fund-links">
            <MFBeaconButton
              type="button"
              id="viewOurMethodologyBtn"
              data-name={generateGeneralTestID('RDF', 'ViewMethodologyButton')}
              className="override-beacon-button-padding"
              variant="tertiary"
              onClick={handleButtonClick}
              label="View Our Methodology"
            />
          </span>
        </div>
        {fundListDisplay && <RDFFragment />}
        <div className="button-container">
          <MFBeaconButton
            variant="secondary"
            size="normal"
            id="RDFBack"
            data-name={generateGeneralTestID('RDF', 'BackButton')}
            onClick={back()}
            label="Back"
          />
        </div>
      </div>
      <PhoneNumber />
      <RDFSelectionLegal />
      <style jsx>{styles}</style>
    </div>
  );
};
/* eslint-enable complexity */

export default RDFScreen;
